import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RoomProduct } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getHasDiscount } from "@tenant/hotels";
import { AccountSelectors } from "@/modules/account";

export type UseHotelRoomNightlyPricingProps = {
  selectedProduct: RoomProduct;
};

export type UseHotelRoomNightlyPricingReturn = {
  nightlyPrice: string;
  nightlyPoints: string;
  showPointsPricing: boolean;
  rewardsAccountId: string | undefined;
};

export const useHotelRoomNightlyPricing = ({
  selectedProduct,
}: UseHotelRoomNightlyPricingProps): UseHotelRoomNightlyPricingReturn => {
  const { formatFiatCurrency, formatRewardsCurrency } = useI18nContext();
  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);

  const accountSpecificPrice =
    rewardsAccountId != null
      ? selectedProduct.tripTotal.accountSpecific?.[rewardsAccountId]
      : undefined;

  const showPointsPricing =
    rewardsAccountId != null &&
    accountSpecificPrice != null &&
    Object.keys(accountSpecificPrice).length > 0;

  const nightlyDA = selectedProduct.nightlyDiscountAwareTaxesAndFeesIncluded;
  const nightlyDiscounted =
    nightlyDA.priceWithAllDiscounts ?? nightlyDA.priceWithUnmanagedDiscounts;
  const nightlyPriceWithNoDiscounts = nightlyDA.priceWithNoDiscounts;

  const hasDiscount = getHasDiscount(nightlyDA);

  const nightlyPriceTotal = hasDiscount
    ? nightlyDiscounted
    : nightlyPriceWithNoDiscounts;

  const nightlyPrice =
    nightlyPriceTotal != null
      ? formatFiatCurrency(nightlyPriceTotal.fiat)
      : formatFiatCurrency(selectedProduct.perNightTotal.fiat);
  const nightlyPoints = rewardsAccountId
    ? (formatRewardsCurrency(
        nightlyPriceTotal?.accountSpecific[rewardsAccountId] ??
          selectedProduct.perNightTotal.accountSpecific[rewardsAccountId],
        { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      ) ?? "")
    : "";

  return useMemo(
    () => ({
      nightlyPrice,
      nightlyPoints,
      showPointsPricing,
      rewardsAccountId,
    }),
    [nightlyPrice, nightlyPoints, showPointsPricing, rewardsAccountId]
  );
};
